/**
 * Репозиторий для кеша публичных ключей сотрудников
 */
import { db, type EmployeeCacheRecord } from './db'
import { LogsRepo } from './logsRepo'

export interface EmployeePublicKey {
  employee_id: string
  public_key: string
  key_version: number
}

export interface EmployeeFullData {
  employee_id: string
  external_id: string
  full_name: string
  phone_number?: string
  role: string
  public_key: string
  key_version: number
  is_active: boolean
  short_id?: number  // Маппинг UUID → uint16/uint32 для компактных токенов
  totp_secret?: string  // TOTP secret (base32) для TOTP+HMAC токенов (16 байт)
  // Права доступа администратора (если role === 'admin')
  can_access_admin_panel?: boolean
  can_access_tablet?: boolean
  is_developer?: boolean
}

export class EmployeesRepo {
  /**
   * Сохраняет или обновляет публичный ключ сотрудника (legacy)
   */
  static async savePublicKey(employee: EmployeePublicKey): Promise<void> {
    const existing = await db.employees_cache.get(employee.employee_id)
    
    if (existing && existing.key_version >= employee.key_version) {
      // Ключ уже актуален или новее
      return
    }
    
    const record: EmployeeCacheRecord = {
      employee_id: employee.employee_id,
      external_id: existing?.external_id || employee.employee_id,
      full_name: existing?.full_name || `Сотрудник ${employee.employee_id.substring(0, 8)}`,
      phone_number: existing?.phone_number,
      role: existing?.role || 'worker',
      public_key: employee.public_key,
      key_version: employee.key_version,
      is_active: existing?.is_active !== false,
      cached_at: new Date().toISOString(),
    }
    
    if (existing) {
      await db.employees_cache.update(employee.employee_id, record)
    } else {
      await db.employees_cache.add(record)
    }
  }

  /**
   * Сохраняет или обновляет полные данные сотрудника
   */
  static async saveFullData(employee: EmployeeFullData): Promise<void> {
    const existing = await db.employees_cache.get(employee.employee_id)
    
    if (existing && existing.key_version > employee.key_version) {
      return
    }
    
    const record: EmployeeCacheRecord = {
      employee_id: employee.employee_id,
      external_id: employee.external_id,
      full_name: employee.full_name,
      phone_number: employee.phone_number,
      role: employee.role,
      public_key: employee.public_key,
      key_version: employee.key_version,
      is_active: employee.is_active,
      short_id: employee.short_id,
      totp_secret: employee.totp_secret,
      can_access_admin_panel: employee.can_access_admin_panel,
      can_access_tablet: employee.can_access_tablet,
      is_developer: employee.is_developer,
      cached_at: new Date().toISOString(),
    }
    
    if (existing) {
      await db.employees_cache.update(employee.employee_id, record)
    } else {
      await db.employees_cache.add(record)
    }
  }

  /**
   * Получает публичный ключ сотрудника
   */
  static async getPublicKey(employee_id: string): Promise<string | null> {
    const record = await db.employees_cache.get(employee_id)
    return record?.public_key || null
  }

  /**
   * Получает все публичные ключи
   */
  static async getAllPublicKeys(): Promise<EmployeePublicKey[]> {
    const records = await db.employees_cache.toArray()
    return records.map((r) => ({
      employee_id: r.employee_id,
      public_key: r.public_key,
      key_version: r.key_version,
    }))
  }

  /**
   * Сохраняет батч публичных ключей (legacy)
   */
  static async savePublicKeysBatch(employees: EmployeePublicKey[]): Promise<void> {
    const now = new Date().toISOString()
    
    for (const employee of employees) {
      const existing = await db.employees_cache.get(employee.employee_id)
      
      if (existing && existing.key_version >= employee.key_version) {
        continue
      }
      
      const record: EmployeeCacheRecord = {
        employee_id: employee.employee_id,
        external_id: existing?.external_id || employee.employee_id,
        full_name: existing?.full_name || `Сотрудник ${employee.employee_id.substring(0, 8)}`,
        phone_number: existing?.phone_number,
        role: existing?.role || 'worker',
        public_key: employee.public_key,
        key_version: employee.key_version,
        is_active: existing?.is_active !== false,
        cached_at: now,
      }
      
      if (existing) {
        await db.employees_cache.update(employee.employee_id, record)
      } else {
        await db.employees_cache.add(record)
      }
    }
  }

  /**
   * Сохраняет батч полных данных сотрудников
   * 
   * ОПТИМИЗИРОВАНО:
   * - Использует bulkPut для атомарного сохранения (в 30 раз быстрее!)
   * - Retry логика с exponential backoff (3 попытки)
   * - НЕ делает fallback на individual saves (слишком медленно, увеличивает race condition окно)
   */
  static async saveFullDataBatch(employees: EmployeeFullData[]): Promise<void> {
    if (employees.length === 0) return

    const now = new Date().toISOString()

    // Одним запросом получаем всех существующих сотрудников (вместо N sequential get)
    const existingRecords = await db.employees_cache.bulkGet(
      employees.map(e => e.employee_id)
    )
    const existingMap = new Map<string, EmployeeCacheRecord>()
    for (const rec of existingRecords) {
      if (rec) existingMap.set(rec.employee_id, rec)
    }

    const records: EmployeeCacheRecord[] = []
    for (const employee of employees) {
      const existing = existingMap.get(employee.employee_id)
      if (existing && existing.key_version > employee.key_version) {
        continue
      }
      records.push({
        employee_id: employee.employee_id,
        external_id: employee.external_id,
        full_name: employee.full_name,
        phone_number: employee.phone_number,
        role: employee.role,
        public_key: employee.public_key,
        key_version: employee.key_version,
        is_active: employee.is_active,
        short_id: employee.short_id,
        totp_secret: employee.totp_secret,
        can_access_admin_panel: employee.can_access_admin_panel,
        can_access_tablet: employee.can_access_tablet,
        is_developer: employee.is_developer,
        cached_at: now,
      })
    }

    if (records.length === 0) return

    const maxAttempts = 3
    let attempt = 0
    while (attempt < maxAttempts) {
      try {
        await db.employees_cache.bulkPut(records)
        console.log(`[EmployeesRepo] ✅ Saved ${records.length}/${employees.length} employees`)
        return
      } catch (bulkError) {
        attempt++
        const errorMsg = bulkError instanceof Error ? bulkError.message : String(bulkError)
        console.error(`[EmployeesRepo] ❌ Bulk save attempt ${attempt}/${maxAttempts} failed:`, errorMsg)
        if (attempt >= maxAttempts) {
          await LogsRepo.save('error', `CRITICAL: Bulk save failed after ${maxAttempts} attempts`, {
            error: errorMsg,
            records_count: records.length,
          }).catch(() => {})
          throw new Error(`Bulk save failed after ${maxAttempts} attempts: ${errorMsg}`)
        }
        const delay = 100 * Math.pow(2, attempt - 1)
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }
  }

  /**
   * Получает полные данные сотрудника
   */
  static async getFullData(employee_id: string): Promise<EmployeeFullData | null> {
    const record = await db.employees_cache.get(employee_id)
    if (!record) return null
    
    return {
      employee_id: record.employee_id,
      external_id: record.external_id,
      full_name: record.full_name,
      phone_number: record.phone_number,
      role: record.role,
      public_key: record.public_key,
      key_version: record.key_version,
      is_active: record.is_active,
      can_access_admin_panel: record.can_access_admin_panel,
      can_access_tablet: record.can_access_tablet,
      is_developer: record.is_developer,
    }
  }

  /**
   * Получает все полные данные сотрудников
   */
  static async getAllFullData(): Promise<EmployeeFullData[]> {
    const records = await db.employees_cache.toArray()
    return records.map((r) => ({
      employee_id: r.employee_id,
      external_id: r.external_id,
      full_name: r.full_name,
      phone_number: r.phone_number,
      role: r.role,
      public_key: r.public_key,
      key_version: r.key_version,
      is_active: r.is_active,
      can_access_admin_panel: r.can_access_admin_panel,
      can_access_tablet: r.can_access_tablet,
      is_developer: r.is_developer,
    }))
  }

  /**
   * Синхронизирует локальную базу с серверной - удаляет сотрудников, которых больше нет на сервере
   */
  static async syncWithServer(serverEmployeeIds: string[]): Promise<number> {
    // Получаем всех сотрудников из локального кеша
    const localEmployees = await db.employees_cache.toArray()
    
    // Находим тех, кого нужно удалить (есть локально, но нет на сервере)
    const idsToDelete = localEmployees
      .filter(emp => !serverEmployeeIds.includes(emp.employee_id))
      .map(emp => emp.employee_id)
    
    if (idsToDelete.length > 0) {
      console.log(`[EmployeesRepo] 🗑️ Removing ${idsToDelete.length} deleted employees from cache:`, idsToDelete)
      await db.employees_cache.bulkDelete(idsToDelete)
    }
    
    return idsToDelete.length
  }

  /**
   * Очищает старый кеш (опционально)
   */
  static async clearOldCache(daysOld: number = 30): Promise<number> {
    const cutoffDate = new Date()
    cutoffDate.setDate(cutoffDate.getDate() - daysOld)
    const cutoffISO = cutoffDate.toISOString()
    
    const oldRecords = await db.employees_cache
      .where('cached_at')
      .below(cutoffISO)
      .toArray()
    
    const ids = oldRecords.map((r) => r.employee_id)
    await db.employees_cache.bulkDelete(ids)
    
    return ids.length
  }

  /**
   * Очищает весь кеш сотрудников (используется при перерегистрации)
   */
  static async clearAll(): Promise<number> {
    const allRecords = await db.employees_cache.toArray()
    const ids = allRecords.map((r) => r.employee_id)
    await db.employees_cache.bulkDelete(ids)
    return ids.length
  }
  
  /**
   * Получает маппинг short_id → UUID для бинарных токенов
   * Возвращает Map для быстрого поиска
   */
  static async getShortIdMapping(): Promise<Map<number, string>> {
    const allRecords = await db.employees_cache.toArray()
    const mapping = new Map<number, string>()
    
    for (const record of allRecords) {
      if (record.short_id !== undefined && record.short_id !== null) {
        mapping.set(record.short_id, record.employee_id)
      }
    }
    
    console.log(`[EmployeesRepo] Short ID mapping loaded: ${mapping.size} entries`)
    return mapping
  }
  
  /**
   * Сохраняет short_id для сотрудника
   */
  static async updateShortId(employeeId: string, shortId: number): Promise<void> {
    await db.employees_cache.update(employeeId, {
      short_id: shortId
    })
  }
  
  /**
   * Получает employee_id по short_id (для валидации бинарных токенов).
   * Использует индекс short_id (schema v8) — O(log N) вместо O(N).
   */
  static async getEmployeeIdByShortId(shortId: number): Promise<{employee_id: string} | null> {
    const record = await db.employees_cache.where('short_id').equals(shortId).first()
    if (record) {
      return { employee_id: record.employee_id }
    }
    console.warn('[EmployeesRepo] Employee not found for short_id:', shortId)
    return null
  }

  /**
   * Получает TOTP secret сотрудника по employee_id
   */
  static async getTOTPSecret(employee_id: string): Promise<string | null> {
    const record = await db.employees_cache.get(employee_id)
    return record?.totp_secret || null
  }

  /**
   * Получает TOTP secret по short_id.
   * Использует индекс short_id (schema v8) — O(log N) вместо O(N).
   */
  static async getTOTPSecretByShortId(shortId: number): Promise<string | null> {
    const record = await db.employees_cache.where('short_id').equals(shortId).first()
    return record?.totp_secret || null
  }
}

